import { NavLink } from 'react-router-dom';
import {
    AnalyticsUp,
    ArrowClockwise,
    ArrowLeft,
    Boat,
    Calculator,
    CaretLeft,
    CaretRight,
    ChartLineUp,
    ClipboardText,
    CurrencyDollar,
    DownloadSimple,
    Drag,
    EnvelopeSimple,
    Gear,
    Home3,
    Lightning,
    Logout,
    Package,
    Rocket,
    ShieldCheck,
    ShippingTruck,
    ShoppingCart,
    Storefront,
    Tag,
    TaskDaily,
    User,
    Users,
    UsersThree,
    Warehouse,
    Warning,
    Wrench,
} from 'shared/icons';
import { useAuth } from '../../hooks/useAuth';
import useIconColor from '../../hooks/useIconColor';

// Página Inicial é a home — item solo, fora de qualquer grupo (não é "Vendas").
const HOME_ITEM = {
    path: '/',
    label: 'Página Inicial',
    Icon: Home3,
    permission: null,
    devices: ['desktop', 'mobile'],
};

// Navegação agrupada por seção (MobileSidebar.jsx espelha a MESMA ordem de grupos).
// Ícones são ÚNICOS por item pra evitar ambiguidade (antes `Users` repetia em
// Clientes/Usuários/Organizações e `AnalyticsUp` em Operações/Pagamentos).
const MENU_GROUPS = [
    {
        // Primeiro grupo do menu (pedido do Thiago, 07/10/2026).
        label: 'Anúncios',
        items: [
            {
                // Rota /campaigns mantida: só o rótulo mudou — a página é o
                // monitoramento operacional dos anúncios/campanhas no Meli.
                path: '/campaigns',
                label: 'Análise Meli',
                Icon: Tag,
                permission: 'campaigns:read',
                devices: ['desktop', 'mobile'],
            },
        ],
    },
    {
        label: 'Vendas',
        items: [
            {
                path: '/operacoes',
                label: 'Operações',
                Icon: AnalyticsUp,
                permission: 'stock:read',
                devices: ['desktop', 'mobile'],
            },
            {
                path: '/orders',
                label: 'Pedidos',
                Icon: Package,
                permission: 'orders:read',
                devices: ['desktop', 'mobile'],
            },
            {
                // Gestão de Pedidos (2026-09-22): todos os pedidos com o
                // pós-venda, as mensagens por pack e a conciliação por pedido
                // integrados. Tela nova em teste — /orders continua de pé.
                path: '/pedidos',
                label: 'Gestão de Pedidos',
                Icon: ShoppingCart,
                permission: 'orders:read',
                devices: ['desktop', 'mobile'],
            },
            {
                path: '/payments',
                label: 'Pagamentos',
                Icon: CurrencyDollar,
                permission: 'payments:read',
                devices: ['desktop'],
            },
            {
                path: '/atendimento',
                label: 'Atendimento',
                Icon: EnvelopeSimple,
                permission: 'atendimento:read',
                devices: ['desktop'],
            },
            {
                path: '/rmas',
                label: 'Pós-venda',
                Icon: ArrowClockwise,
                permission: 'rmas:read',
                devices: ['desktop'],
            },
            {
                // "RMAs" = lotes ao fabricante (renomeado de "Garantia" em
                // 2026-09-22; a rota /garantia é a mesma).
                path: '/garantia',
                label: 'RMAs',
                Icon: ShieldCheck,
                permission: 'rmas:read',
                devices: ['desktop'],
            },
            {
                // Inteligência de Mercado (Fase 4) promovida a página própria:
                // briefing, relatórios e análises são leitura de VENDAS, não a
                // operação de campanhas — esta fica no grupo Anúncios.
                path: '/market-intelligence',
                label: 'Inteligência de Mercado',
                Icon: ChartLineUp,
                permission: 'campaigns:read',
                devices: ['desktop', 'mobile'],
            },
            {
                // Precificação: custo líquido por tratamento de ICMS, regras por
                // SKU/NCM e NF-e de compra. Leitura de custo é dado sensível —
                // permissão própria (pricing:read), não herda de campanhas.
                path: '/precificacao',
                label: 'Precificação',
                Icon: Calculator,
                permission: 'pricing:read',
                devices: ['desktop'],
            },
            {
                // Conferência ao vivo da API do Mercado Livre. Só desktop: a
                // tela existe para ler JSON cru lado a lado com a documentação,
                // e isso não se faz no celular.
                path: '/ml-explorer',
                label: 'Explorador de Endpoints',
                Icon: Wrench,
                permission: 'campaigns:read',
                devices: ['desktop'],
            },
        ],
    },
    {
        label: 'Compras & Logística',
        items: [
            {
                path: '/purchasing',
                label: 'Ordens de compra',
                Icon: TaskDaily,
                permission: 'purchasing:read',
                devices: ['desktop', 'mobile'],
            },
            {
                // Navios das importações (Ásia → Santos): posição, destino e
                // ETA. Mobile primeiro — o dono acompanha pelo celular.
                path: '/navios',
                label: 'Navios',
                Icon: Boat,
                permission: 'navios:read',
                devices: ['desktop', 'mobile'],
            },
            {
                path: '/wms',
                label: 'WMS',
                Icon: ShippingTruck,
                permission: 'wms:read',
                devices: ['desktop', 'mobile'],
            },
            {
                // Coletas fica FORA do Armazém de propósito: é a camada a
                // montante do WMS (a fila de envios que vira OS), não um
                // cadastro dele. Sem ela não existe picking nem packing.
                path: '/coletas',
                label: 'Coletas',
                Icon: Package,
                permission: 'wms:read',
                devices: ['desktop', 'mobile'],
            },
        ],
    },
    {
        // Módulo WMS novo (M0/M1): "Armazém" com Configurações, Localizações e
        // Coletor. `end: true` no /warehouse evita o item acender junto nas
        // rotas filhas (/warehouse/locations, /warehouse/collector).
        label: 'Armazém',
        items: [
            {
                path: '/warehouse',
                label: 'Configurações',
                Icon: Gear,
                // r4: cada aba tem permissão própria — o item aparece para quem
                // tem QUALQUER uma (o backend expande wms:manage em todas).
                anyOfPermissions: [
                    'wms:manage:operators',
                    'wms:manage:equipment',
                    'wms:manage:lpn_prefixes',
                    'wms:manage:service_order_types',
                    'wms:manage:deposits',
                    'wms:manage:deposit_rules',
                    'wms:manage:products',
                    'wms:manage:printers',
                    'wms:manage:settings',
                ],
                devices: ['desktop'],
                end: true,
            },
            {
                // Rótulo espelha a chave `wms.tab.locations` do dicionário do
                // módulo (fallback em components/warehouse/warehouseLabels.js);
                // o menu é config estática, então fica o literal PT-BR.
                path: '/warehouse/locations',
                label: 'Localizações',
                Icon: Warehouse,
                permission: 'wms:manage:locations',
                devices: ['desktop'],
            },
            {
                // M3 — LPN & Recebimento. Só desktop: receber mercadoria é
                // trabalho de console (o coletor tem o fluxo guiado dele).
                path: '/warehouse/lpns',
                label: 'LPNs',
                Icon: Package,
                permission: 'wms:manage:lpns',
                devices: ['desktop'],
            },
            {
                // INBOUND contra OC (20/08): o recebimento ORIENTADO ao pedido
                // de compra — esperado × recebido, parciais, divergência à
                // vista e confronto com a NF do fornecedor. O /warehouse/lpns
                // continua sendo o avulso (pallet sem OC).
                path: '/warehouse/inbound',
                label: 'Recebimento (OC)',
                Icon: DownloadSimple,
                permission: 'wms:manage:inbound',
                devices: ['desktop'],
            },
            {
                // ENTRADA DE DEVOLUÇÕES (20/08): chegada + separação (volta ao
                // vendável × retorno ao fornecedor). Independente do módulo de
                // pós-venda (rmas) — integração é fase futura.
                path: '/warehouse/returns',
                label: 'Devoluções (entrada)',
                Icon: ArrowLeft,
                permission: 'wms:manage:returns',
                devices: ['desktop'],
            },
            {
                // M5 — quadro de OSs + alocação com preview de split +
                // comparador de rotas. A alocação NÃO ganha item próprio: é
                // uma visão da mesma página (o resultado dela cai no quadro).
                path: '/warehouse/service-orders',
                label: 'Ordens de Serviço',
                Icon: TaskDaily,
                permission: 'wms:manage:service_orders',
                devices: ['desktop'],
            },
            {
                // M4 — fila de incidentes do chão (ocupação não cadastrada,
                // pallet ausente, bipe divergente). Console do Supervisor, como
                // Localizações e LPNs: página própria, permissão própria.
                path: '/warehouse/incidents',
                label: 'Incidentes',
                Icon: Warning,
                permission: 'wms:manage:incidents',
                devices: ['desktop'],
            },
            {
                // M7 — REPACK & Picking fracionado. Console do Supervisor:
                // dividir, juntar/transferir e separar fracionado (que NÃO gera
                // pallet). Página própria porque é onde o saldo troca de lugar
                // — e onde a conta antes × depois é conferida antes de gravar.
                path: '/warehouse/repack',
                label: 'Repack',
                Icon: Drag,
                permission: 'wms:manage:repack',
                devices: ['desktop'],
            },
            {
                // M6 — console da fábrica: OPs, lotes/genealogia e os cadastros
                // de produção (máquinas, ficha técnica, opções de kit).
                path: '/warehouse/production',
                label: 'Fábrica',
                Icon: Wrench,
                permission: 'wms:manage:production',
                devices: ['desktop'],
            },
            {
                // M8 — console de expedição: checklist do romaneio, a trava de
                // 100% e o fechamento (baixa + espelho Bling). O romaneio em si
                // continua no item "WMS" — aqui é a sessão de CARREGAMENTO.
                path: '/warehouse/shipping',
                label: 'Expedição',
                Icon: ShippingTruck,
                permission: 'wms:manage:shipping',
                devices: ['desktop'],
            },
            {
                // M9 — INVENTÁRIO: ocupação/heatmap, espaço ocioso e custo, SLA,
                // FIFO/aging e a CONCILIAÇÃO com a trilha. É também de onde sai
                // a OS de contagem (os blocos são escolhidos no heatmap).
                path: '/warehouse/inventory',
                label: 'Inventário',
                Icon: AnalyticsUp,
                permission: 'wms:manage:inventory',
                devices: ['desktop'],
            },
            {
                path: '/warehouse/collector',
                label: 'Coletor',
                Icon: ClipboardText,
                permission: 'wms:operate',
                devices: ['desktop', 'mobile'],
            },
            {
                // M6 — a BANCADA. Entrada própria porque quem opera a máquina
                // passa o turno nela: obrigá-lo a entrar pelo Coletor e escolher
                // no menu, a cada volta, é atrito diário. Mesma permissão de
                // chão (`wms:operate`) e a mesma identificação por crachá.
                path: '/warehouse/bench',
                label: 'Bancada',
                Icon: Lightning,
                permission: 'wms:operate',
                devices: ['desktop', 'mobile'],
            },
        ],
    },
    {
        label: 'Administração',
        items: [
            {
                path: '/customers',
                label: 'Clientes',
                Icon: UsersThree,
                permission: 'customers:read',
                devices: ['desktop', 'mobile'],
            },
            {
                // Entidade de SISTEMA (pessoas ≠ contas de login): o WMS
                // vincula funcionários deste cadastro como operadores.
                path: '/funcionarios',
                label: 'Funcionários',
                Icon: User,
                permission: 'employees:read',
                devices: ['desktop'],
            },
            {
                // Catálogo de produtos: cadastro nosso; o Bling guarda saldo, NF e kits.
                path: '/produtos',
                label: 'Produtos',
                Icon: Package,
                permission: 'stock:read',
                devices: ['desktop'],
            },
            {
                path: '/users',
                label: 'Usuarios',
                Icon: Users,
                permission: 'users:read',
                devices: ['desktop'],
            },
            {
                path: '/organizations',
                label: 'Organizacoes',
                Icon: Storefront,
                permission: 'organizations:read',
                devices: ['desktop'],
            },
            {
                path: '/health',
                label: 'Health',
                Icon: ShieldCheck,
                permission: 'health:read',
                devices: ['desktop'],
            },
        ],
    },
];

// "Almofada" de fundo ao redor do ícone: quadrado fixo (40x40) centralizado
// nele. Colapsado, ELA carrega o bg ativo/hover (fica 1:1 com o ícone, não
// com o link inteiro — antes o bg vinha do NavLink, cujo padding assimétrico
// pl-5/pr-3 deixava o retângulo deslocado pra esquerda). Expandido, o bg
// passa a viver no NavLink (link inteiro) e o slot fica sem cor própria.
const IconSlot = ({ isExpanded, isActive, children }) => (
    <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 ${
            isExpanded
                ? ''
                : isActive
                  ? 'bg-mono-100 dark:bg-mono-900'
                  : 'group-hover:bg-mono-50 dark:group-hover:bg-mono-950'
        }`}
    >
        {children}
    </span>
);

// Cabeçalho de grupo com altura FIXA (h-6): texto (expandido) e linha
// divisória (colapsado) ficam sobrepostos via position:absolute, alternando
// só a opacidade — nunca mudam o fluxo/altura do container, então os
// NavLinks abaixo não pulam verticalmente na transição (eram 2 elementos de
// tamanho diferente antes, um substituindo o outro). `whitespace-nowrap` no
// texto evita a quebra em 2 linhas visível no meio da transição de largura.
const GroupHeader = ({ label, isExpanded }) => (
    <div className="relative h-6">
        <p
            className={`absolute inset-x-3 bottom-1 overflow-hidden whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-mono-400 transition-opacity duration-200 dark:text-mono-600 ${
                isExpanded ? 'opacity-100' : 'opacity-0'
            }`}
        >
            {label}
        </p>
        <div
            className={`absolute inset-x-2 bottom-2 border-t border-mono-200 transition-opacity duration-200 dark:border-mono-800 ${
                isExpanded ? 'opacity-0' : 'opacity-100'
            }`}
            aria-hidden="true"
        />
    </div>
);

const Sidebar = ({ isCollapsed, toggleSidebar, expandedVisually, onHoverChange }) => {
    const { user, logout, checkPermission } = useAuth();
    const iconColor = useIconColor();
    // expandedVisually é true quando isCollapsed=false OU quando isCollapsed=true mas
    // o cursor está sobre a sidebar (hover-to-open em overlay).
    const isExpanded = expandedVisually !== undefined ? expandedVisually : !isCollapsed;
    const isOverlay = isCollapsed && isExpanded;
    const handleMouseEnter = () => {
        if (typeof onHoverChange === 'function' && isCollapsed) {
            onHoverChange(true);
        }
    };
    const handleMouseLeave = () => {
        if (typeof onHoverChange === 'function') {
            onHoverChange(false);
        }
    };

    const hasPermission = (permission) => {
        return checkPermission(permission);
    };

    // Filtro de permissão por item: `permission` (única) ou `anyOfPermissions`
    // (basta UMA — páginas multi-responsável, ex.: Configurações do Armazém em
    // que cada aba tem permissão própria). Grupos sem itens visíveis somem.
    const visibleGroups = MENU_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) =>
            item.anyOfPermissions?.length
                ? item.anyOfPermissions.some((key) => hasPermission(key))
                : hasPermission(item.permission),
        ),
    })).filter((group) => group.items.length > 0);
    // Largura efetiva: expande para 64 quando hover com isCollapsed (overlay), senão 20.
    const widthClass = isExpanded ? 'lg:w-64' : 'lg:w-20';
    const overlayClass = isOverlay ? 'lg:shadow-2xl' : '';

    // Largura do rótulo revelado ao lado do ícone. Sempre montado no DOM
    // (nunca condicional) + `overflow-hidden`/`whitespace-nowrap` + max-width
    // animado: o texto NUNCA quebra pra baixo do ícone nem estoura largura
    // durante a transição do sidebar — só cresce/encolhe de forma contida,
    // em vez de aparecer/desaparecer de um frame pro outro.
    // `min-w-0` é obrigatório: o span é filho de um flex container (o
    // NavLink), e min-width:auto (default de item flex) usaria a largura
    // total do texto (por causa do nowrap) e ignoraria o max-width:0 abaixo.
    const labelSpanClass = `block min-w-0 overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ${isExpanded ? 'opacity-100' : 'opacity-0'}`;
    const labelSpanStyle = { maxWidth: isExpanded ? 200 : 0 };

    return (
        <aside
            aria-label="Sidebar de navegação"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 overflow-hidden bg-white dark:bg-mono-950 border-r border-mono-200 dark:border-mono-800 z-40 transition-all duration-200 ${widthClass} ${overlayClass}`}
        >
            {/* Header: ícone Rocket ↔ nome "Melidash", sobrepostos (opacity)
                numa altura FIXA (h-8, do tamanho do ícone) — o swap instantâneo
                antes usava `h1` (altura de linha diferente do ícone) trocando
                o CONTEÚDO, então a altura real do header mudava ~4px entre os
                dois estados e deslocava TODO o nav abaixo dele verticalmente.
                Fora da área de scroll — fica sempre visível. */}
            <div className="relative h-8 shrink-0 px-4 pt-5 mb-8">
                <div
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${isExpanded ? 'opacity-0' : 'opacity-100'}`}
                >
                    <Rocket size={32} weight="bold" className="text-black dark:text-white" />
                </div>
                <div
                    className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
                >
                    <h1 className="text-xl font-bold text-black dark:text-white">Melidash</h1>
                </div>
            </div>

            {/* Única área com scroll (descontando header/footer). Scrollbar-gutter
                reserva o espaço sempre — o thumb só pinta no hover (.sidebar-nav-scroll,
                ver index.css), então o hover nunca estreita/desloca os itens.
                `overscroll-contain`: sem isso, ao atingir o topo/fundo da
                lista o scroll "vaza" (chaining) e continua rolando a PÁGINA
                por trás — mesmo com o mouse dentro do nav. */}
            <nav className="sidebar-nav-scroll overscroll-contain flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 space-y-4">
                {/* Página Inicial: item solo, não é "Vendas" nem nenhuma categoria. */}
                <div className="space-y-1">
                    <NavLink
                        to={HOME_ITEM.path}
                        title={isExpanded ? undefined : HOME_ITEM.label}
                        className={({ isActive }) =>
                            `group flex items-center rounded-lg py-1 pl-3 pr-3 text-sm font-medium transition-colors duration-200 ${
                                isExpanded
                                    ? isActive
                                        ? 'bg-mono-100 dark:bg-mono-900 text-black dark:text-white'
                                        : 'text-mono-600 dark:text-mono-400 hover:bg-mono-50 dark:hover:bg-mono-950 hover:text-black dark:hover:text-white'
                                    : isActive
                                      ? 'text-black dark:text-white'
                                      : 'text-mono-600 dark:text-mono-400 hover:text-black dark:hover:text-white'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <IconSlot isExpanded={isExpanded} isActive={isActive}>
                                    <HOME_ITEM.Icon size={24} style={{ color: isActive ? iconColor : undefined }} />
                                </IconSlot>
                                <span className={`ml-1 ${labelSpanClass}`} style={labelSpanStyle}>
                                    {HOME_ITEM.label}
                                </span>
                            </>
                        )}
                    </NavLink>
                </div>

                {visibleGroups.map((group) => (
                    <div key={group.label}>
                        <GroupHeader label={group.label} isExpanded={isExpanded} />
                        <div className="space-y-1">
                            {group.items.map((item) => {
                                const Icon = item.Icon;
                                return (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        end={item.end}
                                        title={isExpanded ? undefined : item.label}
                                        className={({ isActive }) =>
                                            `group flex items-center rounded-lg py-1 pl-3 pr-3 text-sm font-medium transition-colors duration-200 ${
                                                isExpanded
                                                    ? isActive
                                                        ? 'bg-mono-100 dark:bg-mono-900 text-black dark:text-white'
                                                        : 'text-mono-600 dark:text-mono-400 hover:bg-mono-50 dark:hover:bg-mono-950 hover:text-black dark:hover:text-white'
                                                    : isActive
                                                      ? 'text-black dark:text-white'
                                                      : 'text-mono-600 dark:text-mono-400 hover:text-black dark:hover:text-white'
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <IconSlot isExpanded={isExpanded} isActive={isActive}>
                                                    <Icon
                                                        size={24}
                                                        style={{ color: isActive ? iconColor : undefined }}
                                                    />
                                                </IconSlot>
                                                <span className={`ml-1 ${labelSpanClass}`} style={labelSpanStyle}>
                                                    {item.label}
                                                </span>
                                            </>
                                        )}
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            {/* Footer: fora da área de scroll — sempre visível. */}
            <div className="shrink-0 border-t border-mono-200 dark:border-mono-800 p-2 pb-4 space-y-2">
                <NavLink
                    to="/profile"
                    className={({ isActive }) =>
                        `group flex items-center rounded-lg py-1 pl-3 pr-3 transition-colors duration-200 ${
                            isExpanded
                                ? isActive
                                    ? 'bg-mono-100 dark:bg-mono-900'
                                    : 'hover:bg-mono-50 dark:hover:bg-mono-950'
                                : ''
                        }`
                    }
                >
                    {({ isActive }) => (
                        <>
                            <IconSlot isExpanded={isExpanded} isActive={isActive}>
                                <User size={24} style={{ color: iconColor }} />
                            </IconSlot>
                            <span className={`ml-1 min-w-0 ${labelSpanClass}`} style={labelSpanStyle}>
                                <p className="text-sm font-medium text-mono-700 dark:text-mono-300 truncate">
                                    {user?.full_name}
                                </p>
                                <p className="text-xs text-mono-500">Ver perfil</p>
                            </span>
                        </>
                    )}
                </NavLink>
                <button
                    type="button"
                    onClick={logout}
                    className={`group w-full flex items-center rounded-lg py-1 pl-3 pr-3 text-left transition-colors duration-200 ${
                        isExpanded ? 'hover:bg-mono-50 dark:hover:bg-mono-950' : ''
                    }`}
                >
                    <IconSlot isExpanded={isExpanded} isActive={false}>
                        <Logout size={24} style={{ color: iconColor }} />
                    </IconSlot>
                    <span className={`ml-1 ${labelSpanClass}`} style={labelSpanStyle}>
                        <span className="text-sm font-medium text-mono-700 dark:text-mono-300">Sair</span>
                    </span>
                </button>
                <button
                    type="button"
                    onClick={toggleSidebar}
                    title={isCollapsed ? 'Travar sidebar aberto' : 'Soltar sidebar (hover-to-open)'}
                    className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-mono-50 dark:hover:bg-mono-950 transition-all text-left text-mono-500 justify-center"
                >
                    {isCollapsed ? <CaretRight size={20} /> : <CaretLeft size={20} />}
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
